import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Space, Typography, message } from 'antd';
import { BaseViewContext } from '../../views/Base';
import PhoneVerificationService from '../../services/PhoneVerification';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const PhoneVerificationModal = ({
  open,
  phoneNumber,
  onCancel,
  onVerified,
}) => {
  const { t } = useTranslation('translation');
  const { base_view_ref } = useContext(BaseViewContext);
  const [step, setStep] = useState('start');
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [cooldownEnd, setCooldownEnd] = useState(null);

  const displayPhone = useMemo(() => (phoneNumber || '').trim(), [phoneNumber]);

  useEffect(() => {
    if (!open) {
      setStep('start');
      setCode('');
      setLoading(false);
      setResendCountdown(0);
      setResendAttempts(0);
      setCooldownEnd(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      if (resendCountdown > 0) {
        setResendCountdown((prev) => Math.max(prev - 1, 0));
      }

      if (cooldownEnd) {
        const remaining = Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000));
        if (remaining === 0) {
          setCooldownEnd(null);
          setResendAttempts(0);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [open, resendCountdown, cooldownEnd]);

  const canResend = resendCountdown === 0 && !cooldownEnd;
  const cooldownRemaining = cooldownEnd ? Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000)) : 0;

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const handleStart = async () => {
    if (!displayPhone) {
      message.error(t('general.invalid_phone_number'));
      return;
    }

    if (!canResend) {
      return;
    }

    setLoading(true);
    try {
      const response = await PhoneVerificationService.start(displayPhone);
      const status = response?.status;

      if (status === 'already_verified') {
        message.success(t('general.phone_already_verified'));
        onVerified?.(displayPhone);
        return;
      }

      if (status && status !== 'pending') {
        message.info(`${t('general.verification_status')}: ${status}`);
      }

      setStep('verify');
      message.success(t('general.verification_code_sent'));
      setResendCountdown(90);
      setResendAttempts((prev) => {
        const next = prev + 1;
        if (next >= 3) {
          setCooldownEnd(Date.now() + 10 * 60 * 1000);
        }
        return next;
      });
    } catch (error) {
      const statusCode = error?.response?.status;
      const data = error?.response?.data?.data;
      if (statusCode === 429 && data?.status === 'active') {
        const retrySeconds = data?.retryAfterSeconds || 0;
        setResendCountdown(retrySeconds);
        message.warning(t('general.resend_too_soon'));
        return;
      }
      if (statusCode === 429 && data?.status === 'cooldown') {
        const retrySeconds = data?.retryAfterSeconds || 0;
        setCooldownEnd(Date.now() + retrySeconds * 1000);
        message.warning(t('general.resend_locked'));
        return;
      }

      const errorMessage = error?.response?.data?.message || t('general.phone_verification_start_failed');
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = async () => {
    if (!code) {
      message.error(t('general.missing_verification_code'));
      return;
    }

    setLoading(true);
    try {
      const response = await PhoneVerificationService.check(displayPhone, code);
      const status = response?.status;

      if (status === 'approved') {
        message.success(t('general.phone_verified'));
        onVerified?.(displayPhone);
        return;
      }

      message.error(t('general.phone_verification_failed'));
    } catch (error) {
      const statusCode = error?.response?.status;
      const data = error?.response?.data?.data;
      if (statusCode === 410 && data?.status === 'expired') {
        message.error(t('general.verification_code_expired'));
        setStep('start');
        return;
      }

      const errorMessage = error?.response?.data?.message || t('general.phone_verification_failed');
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('general.verify_phone_number')}
      centered
      destroyOnClose
      onCancel={onCancel}
      footer={null}
      getContainer={() => base_view_ref.current}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Text type="secondary">{t('general.phone_number_label')}: {displayPhone || t('general.not_set')}</Text>
        {step === 'start' && (
          <>
            <Text type="secondary">{t('general.phone_verification_instructions')}</Text>
            <Button type="primary" onClick={handleStart} loading={loading} disabled={!displayPhone || !canResend}>
              {t('general.send_verification_code')}
            </Button>
          </>
        )}
        {step === 'verify' && (
          <>
            <Form layout="vertical">
              <Form.Item label={t('general.verification_code')}>
                <Input
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder={t('general.enter_verification_code')}
                />
              </Form.Item>
            </Form>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Button onClick={handleStart} disabled={loading || !canResend}>
                {t('general.resend_code')}
              </Button>
              <Button type="primary" onClick={handleCheck} loading={loading}>
                {t('general.verify')}
              </Button>
            </Space>
              {cooldownEnd ? (
                <Text type="secondary">
                  {t('general.resend_cooldown')} {formatCountdown(cooldownRemaining)}
                </Text>
              ) : (
                <Text type="secondary">
                  {t('general.resend_available_in')} {formatCountdown(resendCountdown)}
                </Text>
              )}
            </Space>
          </>
        )}
      </Space>
    </Modal>
  );
};

export default PhoneVerificationModal;
